<template>
  <div class="action-log-character-filter">
    <div class="filter-bar">
      <span class="gm-label filter-label">Filter</span>
      <div class="active-filters" v-if="selectedCharacterIds.length > 0">
        <button
          v-for="characterId in selectedCharacterIds"
          :key="characterId"
          type="button"
          class="gm-chip filter-tag"
          :title="`Remove filter`"
          @click="removeFilter(characterId)"
        >
          <AvatarImage
            :char-id="characterId"
            :gm-mode="true"
            class="filter-avatar"
          />
          <span class="filter-tag__remove" aria-hidden="true">×</span>
        </button>
      </div>
      <span v-else class="filter-hint">everyone</span>
      <button
        class="gm-btn gm-btn--sm gm-btn--ghost filter-add"
        @click="openCharacterSelector"
        :disabled="selectedCharacterIds.length >= maxFilters"
        :title="`Add a character filter (${selectedCharacterIds.length}/${maxFilters})`"
      >
        +
      </button>
      <button
        class="gm-btn gm-btn--sm gm-btn--ghost filter-reset"
        @click="resetAllFilters"
        v-if="selectedCharacterIds.length > 0"
        title="Clear every filter"
      >
        Clear
      </button>
    </div>

    <!-- Character Selector Modal -->
    <div v-if="showCharSelector" class="modal-overlay" @click="closeCharacterSelector">
      <div class="gm-panel gm-panel--raised modal-container" @click.stop>
        <GmCharacterPicker
          class="modal-picker"
          :characters="availableCharacters"
          title="Filter the log by"
          @select="handleCharacterSelected"
        />
        <button class="gm-btn gm-btn--ghost gm-btn--sm modal-close" title="Close"
                @click="closeCharacterSelector">×</button>
      </div>
    </div>
  </div>
</template>

<script>
import AvatarImage from "@/components/Character/AvatarImage.vue";
import GmCharacterPicker from "@/components/GameMaster/Character/GmCharacterPicker.vue";

export default {
  name: "ActionLogCharacterFilter",
  components: {
    AvatarImage,
    GmCharacterPicker,
  },
  props: {
    characters: {
      type: Array,
      required: true,
    },
    npcCharacters: {
      type: Array,
      default: () => [],
    },
    maxFilters: {
      type: Number,
      default: 5,
    },
  },
  data() {
    return {
      selectedCharacterIds: [],
      showCharSelector: false,
    };
  },
  computed: {
    availableCharacters() {
      return this.characters.concat(this.npcCharacters);
    },
  },
  methods: {
    openCharacterSelector() {
      if (this.selectedCharacterIds.length < this.maxFilters) {
        this.showCharSelector = true;
      }
    },
    closeCharacterSelector() {
      this.showCharSelector = false;
    },
    handleCharacterSelected(characterId) {
      if (!this.selectedCharacterIds.includes(characterId) &&
          this.selectedCharacterIds.length < this.maxFilters) {
        this.selectedCharacterIds.push(characterId);
        this.emitFilterChange();
      }
      this.closeCharacterSelector();
    },
    removeFilter(characterId) {
      const index = this.selectedCharacterIds.indexOf(characterId);
      if (index > -1) {
        this.selectedCharacterIds.splice(index, 1);
        this.emitFilterChange();
      }
    },
    resetAllFilters() {
      this.selectedCharacterIds = [];
      this.emitFilterChange();
    },
    emitFilterChange() {
      this.$emit('filter-change', this.selectedCharacterIds);
    },
  },
};
</script>

<style scoped>
/* One strip, not a panel: in a rail this narrow the filter is a control, and
   whoever is filtered reads faster as their own portrait than as their name. */
.action-log-character-filter {
  margin-bottom: var(--gm-gap-sm);
}

.filter-bar {
  display: flex;
  align-items: center;
  gap: var(--gm-gap-xs);
  padding-bottom: var(--gm-gap-xs);
  border-bottom: 1px solid var(--gm-border-dim);
}

.filter-label {
  flex: none;
}

.filter-hint {
  flex: 1;
  font-size: var(--gm-text-xs);
  color: var(--gm-text-dim);
  font-style: italic;
}

.active-filters {
  flex: 1;
  display: flex;
  flex-wrap: wrap;
  gap: var(--gm-gap-xs);
  min-width: 0;
}

.filter-tag {
  gap: 0.15rem;
  padding: 0.1rem 0.25rem 0.1rem 0.1rem;
  border: none;
  cursor: pointer;
  font: inherit;
}

.filter-avatar {
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 50%;
  object-fit: cover;
  object-position: top;
}

.filter-tag__remove {
  color: var(--gm-accent);
  font-size: 0.9rem;
  line-height: 1;
}

.filter-add,
.filter-reset {
  flex: none;
  padding: 0.05rem 0.4rem;
}

.modal-picker {
  width: min(70rem, 88vw);
  height: min(38rem, 80vh);
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-container {
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  display: flex;
  overflow: hidden;
}

.modal-close {
  position: absolute;
  top: var(--gm-gap-sm);
  right: var(--gm-gap-sm);
  z-index: 2;
  width: 1.75rem;
  height: 1.75rem;
  padding: 0;
  border-radius: 50%;
  line-height: 1;
}
</style>